/** Product anchor bars share their measured occupied height without changing header state. */
const anchorBars = new Set();
const anchorSections = new WeakMap();
let frame = 0;
let resizeObserver;
let lifecycleController;

function visibleBars() {
  return [...anchorBars]
    .filter((bar) => {
      const rect = bar.getBoundingClientRect();
      return bar.isConnected && rect.width > 0 && rect.height > 0 && getComputedStyle(bar).visibility !== 'hidden';
    })
    .sort((a, b) => a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
}

function updateOffsets() {
  frame = 0;
  const visible = visibleBars();
  const visibleSet = new Set(visible);
  // Read every height before writing the component-local custom properties.
  const heights = visible.map((bar) => anchorSections.get(bar)?.getBoundingClientRect().height || bar.getBoundingClientRect().height);
  let offset = 0;
  const offsets = visible.map((bar, index) => {
    const result = { bar, offset };
    offset += heights[index];
    return result;
  });
  for (const bar of anchorBars) {
    if (!visibleSet.has(bar)) anchorSections.get(bar)?.style.removeProperty('--product-anchor-stack-offset');
  }
  for (const { bar, offset: value } of offsets) {
    const section = anchorSections.get(bar);
    const token = `${value}px`;
    if (section && section.style.getPropertyValue('--product-anchor-stack-offset') !== token) {
      section.style.setProperty('--product-anchor-stack-offset', token);
    }
  }
}

function scheduleOffsets() {
  if (!frame) frame = requestAnimationFrame(updateOffsets);
}

function connectBar(bar) {
  if (!anchorBars.size) {
    lifecycleController = new AbortController();
    const { signal } = lifecycleController;
    window.addEventListener('resize', scheduleOffsets, { passive: true, signal });
    document.addEventListener('shopify:section:load', scheduleOffsets, { signal });
    document.addEventListener('shopify:section:unload', scheduleOffsets, { signal });
    resizeObserver = new ResizeObserver(scheduleOffsets);
  }
  anchorSections.set(bar, bar.closest('.shopify-section'));
  anchorBars.add(bar);
  resizeObserver.observe(bar);
  scheduleOffsets();
}

function disconnectBar(bar) {
  resizeObserver?.unobserve(bar);
  anchorBars.delete(bar);
  anchorSections.get(bar)?.style.removeProperty('--product-anchor-stack-offset');
  anchorSections.delete(bar);
  if (!anchorBars.size) {
    lifecycleController?.abort();
    lifecycleController = undefined;
    resizeObserver?.disconnect();
    resizeObserver = undefined;
    cancelAnimationFrame(frame);
    frame = 0;
  } else {
    scheduleOffsets();
  }
}

class ProductAnchorTabs extends HTMLElement {
  connectedCallback() {
    connectBar(this);
  }

  disconnectedCallback() {
    disconnectBar(this);
  }

  /** Visible inner bar heights preserve the released single-bar anchor-scroll margin. */
  static visibleHeight() {
    return visibleBars().reduce((height, bar) => height + bar.getBoundingClientRect().height, 0);
  }
}

if (!customElements.get('product-anchor-tabs')) {
  customElements.define('product-anchor-tabs', ProductAnchorTabs);
}
